import NiceModal from '@ebay/nice-modal-react'
import { classNames } from '@/util/tailwindHelpers'
import React, { useState } from 'react'
import { MEDIA_SIZE, MEDIA_SIZES } from '@/constants'
import { MODAL_ID } from '@/constants/modalId'
import { PreviewMedia } from '..'
import Icon from '@/components/Icon'
import Button from '@/components/Button'
import { MediaLoadingSkeleton } from '@/components/MediaLoadingSkeleton'
import { hasRedeemableMedia, isRedeemable } from '@/util/assetHelpers'
import { Asset } from '@/api/resources/asset/get/schema'
import RedeemableMedia from '../RedeemableMedia'
import { MediaItem, mediaNameEnum } from '@/api/resources/shared/media'
import { isPfp } from '@/api/resources/shared/drop'

const findFrontMediaItem = (mediaItems: MediaItem[]) =>
  mediaItems.find(
    mi =>
      mi.name == mediaNameEnum.enum.video || mi.name === mediaNameEnum.enum.img
  )

const findBackMediaItem = (mediaItems: MediaItem[]) =>
  mediaItems.find(mi => mi.name === mediaNameEnum.enum.backimg)

const InventoryPreviewMedia = ({
  inventory,
  isProductPage,
  className = ''
}: {
  isProductPage?: boolean
  inventory: {
    media?: Asset['media']
    attributes?: Asset['attributes']
    drop_type?: Asset['drop_type']
    traits?: Asset['traits']
    rarity?: Asset['rarity']
    variant?: Asset['variant']
    redeemable?: Asset['redeemable']
    name?: Asset['name']
  }
  className: string
}) => {
  const [showFront, setShowFront] = useState(true)
  const activeMediaIndex = showFront ? 0 : 1

  const { media, attributes, traits, rarity, redeemable } = inventory
  let mediaItems = media || []

  let redeemableMediaQuality = undefined

  if (isProductPage) {
    redeemableMediaQuality = {
      token: MEDIA_SIZE.small,
      box: MEDIA_SIZE.large
    }
  }

  const [mediaLoaded, setMediaLoaded] = useState(
    Array(mediaItems.length).fill(false)
  )

  const isRedeemableRarity = isRedeemable(rarity)
  const isRedeemableExpired = isRedeemableRarity && !redeemable
  const showRedeemableMedia = hasRedeemableMedia(inventory) && showFront

  // If the item is redeemable and expired, we only show the token media
  if (isRedeemableRarity && isRedeemableExpired) {
    mediaItems = mediaItems.slice(0, 1)
  }

  const showToggleButton = mediaItems.length > 1

  // Active Media Item
  let activeMediaItem = showFront
    ? findFrontMediaItem(mediaItems)
    : findBackMediaItem(mediaItems)

  let mediaAspect = activeMediaItem

  // On active redemption tokens we swap the activeMediaItem - Josh Dobson - 4/8/25
  if (isRedeemableRarity && !isRedeemableExpired) {
    if (activeMediaIndex === 1) {
      activeMediaItem = mediaItems[0]
      mediaAspect = mediaItems[0]
    } else {
      mediaAspect = mediaItems[1]
    }
  }

  const shouldRoundCorners =
    isPfp(inventory) ||
    attributes?.rarity === 'Unique Webbian' ||
    traits?.length > 0

  const handleOpenMedia = () => {
    const assets = mediaItems.map(m => {
      const variant = inventory.attributes?.variant || inventory.variant
      return {
        name: `${inventory.name} ${variant ? `- ${variant}` : ''}`,
        media: m
      }
    })
    NiceModal.show(MODAL_ID.inventoryPreview, {
      assets,
      defaultIndex: activeMediaIndex
    })
  }

  const handleToggleClick = () => setShowFront(!showFront)

  const handleOnMediaLoaded = () => {
    setMediaLoaded(prev => {
      const result = prev.slice()
      result.splice(activeMediaIndex, 1, true)
      return result
    })
  }

  return (
    <>
      <div
        className={classNames(
          'h-[378px] aspect-[--aspect] mx-auto flex relative max-w-full',
          className
        )}
        style={{
          '--aspect': `${mediaAspect[MEDIA_SIZES.DEFAULT.width]}/${
            mediaAspect[MEDIA_SIZES.DEFAULT.height]
          }`
        }}
      >
        {!mediaLoaded[activeMediaIndex] && (
          <MediaLoadingSkeleton className="absolute inset-0" />
        )}

        {showRedeemableMedia ? (
          <RedeemableMedia
            tokenClassName={classNames('-translate-x-1/4', {
              'max-sm:-translate-x-2': isProductPage
            })}
            media={mediaItems}
            quality={redeemableMediaQuality}
            onMediaLoaded={handleOnMediaLoaded}
            onClick={handleOpenMedia}
          />
        ) : (
          <PreviewMedia
            className={classNames('max-w-full max-h-full', {
              'rounded-[45px]': shouldRoundCorners
            })}
            size={MEDIA_SIZES.LARGE}
            media={activeMediaItem}
            onClick={handleOpenMedia}
            onLoaded={handleOnMediaLoaded}
          />
        )}
      </div>
      {showToggleButton && <ToggleButton onClick={handleToggleClick} />}
    </>
  )
}

const ToggleButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button theme="clean" className="block m-auto mt-2" onClick={onClick}>
      <Icon name="flipCard" />
    </Button>
  )
}

export default InventoryPreviewMedia
